import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { useCandidates, useCreateOffer, useOffer, useUpdateOffer } from '../queries';
import { OFFER_STATUSES } from '../constants';
import type { Candidate, Offer, OfferInput } from '../types';
import { candidateName } from '../types';

const OFFERS_PATH = '/hrm/recruitment/offers';

const schema = z.object({
  candidate: z.string().min(1, 'Candidate is required'),
  amount: z
    .string()
    .refine((v) => v === '' || (!Number.isNaN(Number(v)) && Number(v) > 0), 'Enter > 0'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  status: z.enum(['draft', 'sent', 'accepted', 'declined']),
  description: z.string(),
});

type FormValues = z.infer<typeof schema>;

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD'] as const;
type Currency = (typeof CURRENCIES)[number];

function toCurrency(value: string | null): Currency {
  return (CURRENCIES as readonly string[]).includes(value ?? '') ? (value as Currency) : 'USD';
}

/**
 * Create + edit share this page. Edit mode holds the form until the offer and
 * the candidate options have loaded, then initialises `useForm` from
 * `defaultValues` — so the Candidate select shows its value on first render.
 */
export function NewOfferPage() {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const { data: existing, isPending: loadingExisting } = useOffer(editId ?? '');
  const { data: candidatesData } = useCandidates();

  if (isEdit && (loadingExisting || !existing || !candidatesData)) {
    return (
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <OfferFormFields
      editId={editId}
      existing={existing ?? null}
      candidates={candidatesData?.results ?? []}
    />
  );
}

function OfferFormFields({
  editId,
  existing,
  candidates,
}: {
  editId?: string;
  existing: Offer | null;
  candidates: Candidate[];
}) {
  const navigate = useNavigate();
  const isEdit = Boolean(editId);
  const create = useCreateOffer();
  const update = useUpdateOffer();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: existing
      ? {
          candidate: String(existing.candidate),
          amount: existing.amount ?? '',
          currency: toCurrency(existing.currency),
          status: existing.status,
          description: existing.description,
        }
      : { candidate: '', amount: '', currency: 'USD', status: 'draft', description: '' },
  });

  const backTo = isEdit ? `${OFFERS_PATH}/${editId}` : OFFERS_PATH;

  async function handleSubmit(values: FormValues) {
    const input: OfferInput = {
      candidate: Number(values.candidate),
      job_opening: existing?.job_opening ?? null,
      amount: values.amount.trim() === '' ? null : values.amount.trim(),
      currency: values.currency,
      status: values.status,
      description: values.description,
      sent_at: existing?.sent_at ?? null,
      responded_at: existing?.responded_at ?? null,
    };
    try {
      if (isEdit && editId) {
        await update.mutateAsync({ id: editId, input });
      } else {
        await create.mutateAsync(input);
      }
      navigate(backTo);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          form.setError(field as keyof FormValues, { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <div className="w-full max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(backTo)}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit offer' : 'New offer'}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormSection variant="card" title="Offer">
            <FormField
              control={form.control}
              name="candidate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Candidate</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a candidate" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {candidates.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {candidateName(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          <FormSection variant="card" title="Status">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OFFER_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection variant="card" title="Description">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <MarkdownEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Offer terms, role summary, benefits, start date…"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <div className="flex gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Create offer'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(backTo)}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
